import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@playbooks/ui/lib/utils'
import { ActionCardsElement } from '@playbooks/action-cards'
import { Button } from '@playbooks/ui/components/button'
import { ViewMode } from '@playbooks/action-cards-utils/src'
import { ArrowRight, CircleCheckBig } from 'lucide-react'

import { SliderManager, Step, StepType, WebPlaySettings } from '../types'
import { isColorDark } from '../utils/isColorDark'
import useCompletionKeywordsMatch from '../hooks/useCompletionKeywordsMatch'

interface SliderDashboardProps {
  viewMode: ViewMode
  accentColor: string
  hasSections: boolean
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  openSheet: () => void
  sliderManagerRef: React.RefObject<SliderManager | null>
  isFinishButtonVisible: boolean
  completionKeywords: Set<string | null> | null
  webplaySettings: WebPlaySettings
}

const SliderDashboard = ({
  viewMode,
  accentColor,
  hasSections,
  actionCardsElementRef,
  openSheet,
  sliderManagerRef,
  isFinishButtonVisible,
  completionKeywords,
  webplaySettings,
}: SliderDashboardProps) => {
  const { t } = useTranslation()
  const [slide, setSlide] = useState<Step | null>(null)

  const finishButtonVisible =
    useCompletionKeywordsMatch({
      actionCardService: null,
      completionKeywords,
      actionCardsElementRef,
    }) && isFinishButtonVisible

  const isAccentColorDark = isColorDark(accentColor)

  const isFirst = slide?.type === StepType.FIRST || slide === null
  const isLast = slide?.type === StepType.LAST
  const isBrowHidden = slide?.type !== StepType.LAST && viewMode === 'funnel'

  const handleNextBtnClick = () => {
    const currentStep = sliderManagerRef.current?.getCurrentStep?.()
    const slides = actionCardsElementRef.current?.getSlides?.()

    if (slides && currentStep) {
      const slide = slides[currentStep.step]

      actionCardsElementRef.current?.highlightElementIds?.(slide?.requiredDataIds || [])

      if (!slides[currentStep.step]?.isFilled) return
    }

    setSlide(sliderManagerRef.current?.nextSlide?.() || null)
  }

  const handlePrevBtnClick = () => {
    setSlide(sliderManagerRef.current?.prevSlide?.() || null)
  }

  return (
    <div
      className={cn('relative flex h-9 w-full', !hasSections && 'hidden', !webplaySettings.enableFunnelView && 'h-0')}
    >
      <SliderButton
        isLeft
        hidden={isFirst}
        onClick={handlePrevBtnClick}
        accentColor={accentColor}
        isAccentColorDark={isAccentColorDark}
        viewMode={viewMode}
      >
        {t('submit.backBtn', 'Back')}
      </SliderButton>
      <div className="ml-auto">
        <SliderButton
          hidden={isLast}
          onClick={handleNextBtnClick}
          accentColor={accentColor}
          isAccentColorDark={isAccentColorDark}
          viewMode={viewMode}
        >
          {t('submit.nextBtn', 'Next')}
        </SliderButton>
      </div>
      <Button
        type="button"
        onClick={openSheet}
        style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
        className={cn(
          !finishButtonVisible && 'hidden',
          !isBrowHidden && 'top-0',
          !webplaySettings.enableFunnelView && '-top-15',
          isAccentColorDark && 'border-zinc-800! border',
          'absolute left-1/2 -translate-x-[50%] transition-[top] max-[410px]:px-2 z-100',
          'text-white',
        )}
      >
        <CircleCheckBig size={16} />

        <span>{t('submit.browLabel', 'Complete')}</span>
      </Button>
    </div>
  )
}

interface SliderButtonProps {
  hidden: boolean
  isLeft?: boolean
  children: React.ReactNode
  onClick: () => void
  viewMode: string
  accentColor: string
  isAccentColorDark: boolean
}

const SliderButton = ({
  isLeft,
  hidden,
  children,
  viewMode,
  accentColor,
  isAccentColorDark,
  onClick,
}: SliderButtonProps) => {
  if (viewMode !== 'funnel') {
    return null
  }

  return hidden ? null : (
    <Button
      onClick={onClick}
      style={{ background: accentColor, borderColor: isAccentColorDark ? undefined : accentColor }}
      className={cn(
        'flex items-center justify-center text-white hover:bg-wp-dark px-4',
        'dark:border dark:border-white gap-2',

        isAccentColorDark && 'border-zinc-800! border border-solid',
      )}
    >
      {isLeft && (
        <div style={{ transform: 'rotate(180deg)' }}>
          <ArrowRight />
        </div>
      )}

      {children}

      {!isLeft && (
        <div>
          <ArrowRight />
        </div>
      )}
    </Button>
  )
}

export default SliderDashboard
